import React from "react";

import styled from "styled-components";
import { DataTypes } from "../../../rule-engine-core/Common/Data/DataTypes";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { DataEditControl } from "../../../CommonUI/DataEditing/DataEditControl";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { IRuleTableDefinition } from "../../../rule-engine-core/Rule/Table/Common/IRuleTableDefinition";
import { ResultCell } from "../../../rule-engine-core/Rule/Table/Common/ResultCell";
import { TableColumn } from "../../../rule-engine-core/Rule/Table/Common/TableColumn";
import { Error } from "./Error";
import {
    ITableRowTestValue,
    TableRowTestContext,
} from "../../../rule-engine-core/Rule/Table/Common/TableRowTestContext";
import { TestingConstants } from "../../../CommonUI/Testing/TestingConstants";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";

export const XResultCell = ({
    ruleDefinition,
    editContext,
    resultCell,
    resultColumn,
    cellIndex,
    rowIndex,
}: {
    ruleDefinition: IRuleTableDefinition;
    editContext: {
        navigate: (direction: "Up" | "Down" | "Right" | "Left" | "Tab" | "Select") => void;
        isSelected: (cell: ResultCell) => boolean;
        setSelected: (cell: ResultCell) => void;
    };
    resultCell: ResultCell;
    resultColumn: TableColumn;
    cellIndex?: number;
    rowIndex?: number;
}) => {
    const value = React.useContext(TableRowTestContext);

    return (
        <ObserveState
            listenTo={editContext}
            properties={["selectedCell"]}
            control={() => {
                let selected = editContext.isSelected(resultCell);
                let validationIssues = ruleDefinition.getResultCellValidationIssues(resultCell);
                return (
                    <ResultCellRender
                        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                            event.stopPropagation();
                            editContext.setSelected(resultCell);
                        }}
                        selected={editContext.isSelected(resultCell)}
                        testValue={value}
                        x={cellIndex}
                        y={rowIndex}
                    >
                        <DataEditControl
                            forData={
                                ruleDefinition.getResultColumnType(resultColumn) ||
                                DataUtilities.createDataByType(resultColumn.dataType!, resultColumn.name)
                            }
                            value={resultCell.value}
                            onUpdate={(newValue) => {
                                resultCell.value = newValue;
                                StateManager.propertyChanged(resultCell, "value");
                                StateManager.changed(ruleDefinition);
                            }}
                            mode={
                                resultColumn.dataType === DataTypes.FILE
                                    ? "display_mode_edit"
                                    : selected
                                      ? "edit_mode"
                                      : "display_mode"
                            }
                            navigate={editContext.navigate}
                            editMode={resultColumn.dataType === DataTypes.FILE ? "layer" : "inline"}
                        />
                        <Error issues={validationIssues} />
                    </ResultCellRender>
                );
            }}
        />
    );
};

const ResultCellRender = styled.div<{ selected: boolean; testValue?: ITableRowTestValue; x?: number; y?: number }>`
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;

    background: ${UIStyleConstants.WHITE_COLOR};
    border-radius: 2px;
    position: relative;

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;

    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    ${(props) =>
        props.selected
            ? `border: 1px solid ${UIStyleConstants.BLUE_GRAY_COLOR}`
            : props.testValue === ITableRowTestValue.PASSED
              ? `border: 1px solid ${TestingConstants.BORDER_SUCCESS}`
              : props.testValue === ITableRowTestValue.FAILED
                ? `border: 1px solid ${TestingConstants.BORDER_FAIL}`
                : `border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR}`};

    ${(props) =>
        props.x !== undefined
            ? `
                grid-column-start: ${props.x + 1};
                grid-column-end: ${props.x + 2};
            `
            : ``}
    ${(props) =>
        props.y !== undefined
            ? `
                grid-row-start: ${props.y + 1};
                grid-row-end: ${props.y + 2};
            `
            : ``}
  .ValueEditor-root {
        width: 100%;
        padding: 6px 4px;

        .ValueEditor-display {
            padding: 6px 12px;

            ${(props) =>
                props.testValue === ITableRowTestValue.PASSED
                    ? ` color: ${UIStyleConstants.GREEN_COLOR};`
                    : props.testValue === ITableRowTestValue.FAILED
                      ? `
                        color: ${UIStyleConstants.RED_COLOR};
                        text-decoration: line-through;
                    `
                      : ``}
        }
    }

    .edit-button {
        opacity: 0;
    }

    &:hover {
        .edit-button {
            opacity: 1;
        }
    }
`;
